import { NextRouter } from 'next/router';

const MAX_HISTORY_LENGTH = 10;
const HISTORY_LOCAL_STORAGE_KEY = '_navHistory';

let initialized = false;

// saving history item to local storage, if it is available
// if history length exceeds MAX_HISTORY_LENGTH, remove the oldest item
export function saveHistoryItemToLocalStorage(item: string) {
  if (typeof window !== 'undefined') {
    const history = JSON.parse(localStorage.getItem(HISTORY_LOCAL_STORAGE_KEY) || '[]');
    if (history.length && history[history.length - 1] === item) {
      return;
    }
    if (history.length >= MAX_HISTORY_LENGTH) {
      history.shift();
    }
    history.push(item);
    localStorage.setItem(HISTORY_LOCAL_STORAGE_KEY, JSON.stringify(history));
  }
}

// fetch history from local storage, if it is available
export function getHistoryFromLocalStorage() {
  if (typeof window !== 'undefined') {
    return JSON.parse(localStorage.getItem(HISTORY_LOCAL_STORAGE_KEY) || '[]');
  }
  return [];
}

/**
 * This react hook is used to keep track of the navigation history.
 *
 * In order to catch all the navigation history you need to run it in the top level component:
 * useNavHistory(router)
 *
 * @returns array for navigation history paths
 */
export function useNavHistory(router: NextRouter) {
  if (initialized || !router.events) {
    return getHistoryFromLocalStorage();
  }
  initialized = true;
  saveHistoryItemToLocalStorage(router.asPath);
  router.events.on('routeChangeStart', (newPath: string) => {
    saveHistoryItemToLocalStorage(newPath);
  });
  return getHistoryFromLocalStorage();
}

export function useNavHistoryHook(path: string | null) {
  if (!initialized) {
    initialized = true;
  }
  const history = getHistoryFromLocalStorage();
  if (path) {
    saveHistoryItemToLocalStorage(path);
  }
  return history;
}
